/* eslint-disable max-lines */
import { accessor } from 'src/store';
import { ChangeLog } from 'src/components/EntityDetail';
import { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { getCellName, getCellType, getMacroPreviousItemData, getScreenPreviousItemData, getScreenUpdateMessages } from './util/applet-design-util';
import isEqual from 'lodash.isequal';
import { AppletChangeLogExtraData } from './applet.entity';
import macroTypeMap from './macros/macroTypeMap';
import type { ScriptOrigin } from '@redviking/argonaut-core-ui/src/applet/design/appletDesign.store';

type BaseTypedPreviousChangeLog = {
    type: AppletChangeLogExtraData['type'];
    changeLogItem: ChangeLog.IncludePreviousChangeLogData;
}

interface ScreenChangeLog extends BaseTypedPreviousChangeLog {
    type: 'screen';
    changeLogItem: ChangeLog.IncludePreviousChangeLogData<PreviousScreenItemData>;
}

interface MacroChangeLog extends BaseTypedPreviousChangeLog {
    type: 'macros';
    changeLogItem: ChangeLog.IncludePreviousChangeLogData<PreviousMacroItemData>;
}

interface ScriptChangeLog extends BaseTypedPreviousChangeLog {
    type: 'scripts';
    changeLogItem: ChangeLog.IncludePreviousChangeLogData<PreviousScriptItemData>;
}

export type TypedPreviousChangeLog = ScreenChangeLog | MacroChangeLog | ScriptChangeLog;

const generateScriptChangeLog = (payload: {
    id: string;
    action: 'update'
    currentScriptValue: string;
    originalScriptValue: Latest.RuntimeScriptCfg;
} | {
    id: string;
    action: 'add';
    currentScriptValue: string;
}): ChangeLog.ChangeLogChange<{
    scriptCfg: Latest.RuntimeScriptCfg;
}> => {
    const title: ChangeLog.Interpolate = {
        text: '',
        type: 'interpolate',
        data: [],
    };

    let messages: ChangeLog.MessageType[] = [];

    if (payload.action === 'add') {
        title.text = 'A script was added and contains the following content:';
        return {
            title,
            itemId: payload.id,
            action: payload.action,
            messages: [
                {
                    type: 'script',
                    oldScriptValue: undefined,
                    currentScriptValue: payload.currentScriptValue,
                },
            ],
        };
    } else if (payload.action === 'update') {
        title.text = 'A script was updated with the following changes:';
        messages = [
            {
                type: 'script',
                oldScriptValue: payload.originalScriptValue.script,
                currentScriptValue: payload.currentScriptValue,
            },
        ];
    }
    return {
        title,
        messages,
        itemId: payload.id,
        action: payload.action,
        previousItem: {
            scriptCfg: payload.originalScriptValue,
        },
    };
};

export type PreviousScreenItemData = {
    screen: Latest.Screen.Config;
    scripts: Latest.RuntimeScriptCfg[];
    macros: Latest.Macros.AppletMacro[];
};

export type PreviousMacroItemData = {
    macroCfg: Latest.Macros.AppletMacro;
    cellData: {
        screenId: string;
        screenIdx: number;
        cellCfgs: {
            cellIdx: number;
            cellCfg: Latest.Screen.Cells.AppletCell;
        }[];
    };
}

export type PreviousScriptItemData = {
    origin: ScriptOrigin;
    scriptCfg: Latest.RuntimeScriptCfg;
};

const getScreenChanges = (originalConfig: Latest.AppletDesignVersionConfig, newConfig: Latest.AppletDesignVersionConfig): ChangeLog.ChangeLogChange<PreviousScreenItemData>[] => {
    const ret: ChangeLog.ChangeLogChange<PreviousScreenItemData>[] = [];
    const newScreens = newConfig.screens;
    const originalScreens = originalConfig.screens;
    for (const screen of newScreens) {
        const originalScreen = originalScreens.find(s => s.id === screen.id);
        if (originalScreen && !isEqual(originalScreen, screen)) {
            ret.push({
                action: 'update',
                itemId: screen.id,
                previousItem: getScreenPreviousItemData(originalConfig, originalScreen),
                title: {
                    type: 'interpolate',
                    text: 'Screen {screenName} was updated',
                    data: [
                        {
                            type: 'code',
                            name: 'screenName',
                            value: screen.name,
                        },
                    ],
                },
                messages: getScreenUpdateMessages({
                    originalScreen,
                    newScreen: screen,
                }),
            });
        } else if (!originalScreen) {
            ret.push({
                messages: [],
                action: 'add',
                itemId: screen.id,
                title: {
                    type: 'interpolate',
                    text: 'Screen {screenName} was added',
                    data: [
                        {
                            type: 'code',
                            name: 'screenName',
                            value: screen.name,
                        },
                    ],
                },
            });
        }
    }

    for (const oldScreen of originalScreens) {
        if (!newScreens.find(s => s.id === oldScreen.id)) {
            ret.push({
                action: 'delete',
                itemId: oldScreen.id,
                previousItem: getScreenPreviousItemData(originalConfig, oldScreen),
                messages: oldScreen.cells.map<ChangeLog.MessageType>(cell => ({
                    messageId: cell.id,
                    type: 'interpolate',
                    text: 'A{cellType}Argonaut cell named{cellName}was removed',
                    data: [
                        {
                            type: 'code',
                            name: 'cellType',
                            value: getCellType(cell),
                        },
                        {
                            type: 'code',
                            name: 'cellName',
                            value: getCellName(cell),
                        },
                    ],
                })),
                title: {
                    type: 'interpolate',
                    text: 'Screen {screenName} was removed',
                    data: [
                        {
                            type: 'code',
                            name: 'screenName',
                            value: oldScreen.name,
                        },
                    ],
                },
            });
        }
    }
    return ret;
};

const getMacroChanges = (originalConfig: Latest.AppletDesignVersionConfig, newConfig: Latest.AppletDesignVersionConfig): ChangeLog.ChangeLogChange<PreviousMacroItemData>[] => {
    const ret: ChangeLog.ChangeLogChange<PreviousMacroItemData>[] = [];
    for (const macro of newConfig.macros) {
        const originalMacro = originalConfig.macros.find(m => m.id === macro.id);
        if (originalMacro && !isEqual(originalMacro, macro)) {
            ret.push({
                messages: [],
                action: 'update',
                itemId: macro.id,
                previousItem: getMacroPreviousItemData(originalConfig, originalMacro),
                title: {
                    type: 'interpolate',
                    text: 'A {macroType} Macro {macroName} was updated',
                    data: [
                        {
                            type: 'code',
                            name: 'macroType',
                            value: macroTypeMap[originalMacro.type].text,
                        },
                        {
                            type: 'code',
                            name: 'macroName',
                            value: originalMacro.name || 'Unnamed Macro',
                        },
                    ],
                },
            });
        } else if (!originalMacro) {
            ret.push({
                messages: [],
                action: 'add',
                itemId: macro.id,
                title: {
                    type: 'interpolate',
                    text: 'A {macroType} Macro {macroName} was added',
                    data: [
                        {
                            type: 'code',
                            name: 'macroType',
                            value: macroTypeMap[macro.type].text,
                        },
                        {
                            type: 'code',
                            name: 'macroName',
                            value: macro.name || 'Unnamed Macro',
                        },
                    ],
                },
            });
        }
    }
    for (const oldMacro of originalConfig.macros) {
        if (!newConfig.macros.find(m => m.id === oldMacro.id)) {
            const oldScreenId = originalConfig.screens.find(s => s.cells.find(c => c.type === 'macroTarget' && c.attrs.macroId === oldMacro.id))?.id;
            if (!oldScreenId) {
                throw new Error('Could not find screen that contained macro');
            }
            const screenThatContainedMacro = newConfig.screens.find(s => s.id === oldScreenId);
            if (screenThatContainedMacro) {
                ret.push({
                    messages: [],
                    action: 'delete',
                    itemId: oldMacro.id,
                    previousItem: getMacroPreviousItemData(originalConfig, oldMacro),
                    title: {
                        type: 'interpolate',
                        text: 'A {macroType} Macro {macroName} was removed',
                        data: [
                            {
                                type: 'code',
                                name: 'macroType',
                                value: macroTypeMap[oldMacro.type].text,
                            },
                            {
                                type: 'code',
                                name: 'macroName',
                                value: oldMacro.name || 'Unnamed Macro',
                            },
                        ],
                    },
                });
            } else {
                ret.push({
                    messages: [],
                    action: 'cascade',
                    itemId: oldMacro.id,
                    title: {
                        type: 'interpolate',
                        text: 'A {macroType} Macro {macroName} was removed because the containing screen was removed',
                        data: [
                            {
                                type: 'code',
                                name: 'macroType',
                                value: macroTypeMap[oldMacro.type].text,
                            },
                            {
                                type: 'code',
                                name: 'macroName',
                                value: oldMacro.name || 'Unnamed Macro',
                            },
                        ],
                    },
                });
            }
        }
    }

    return ret;
};

const getScriptChanges = (originalConfig: Latest.AppletDesignVersionConfig, newConfig: Latest.AppletDesignVersionConfig): ChangeLog.ChangeLogChange<{
    scriptCfg: Latest.RuntimeScriptCfg;
}>[] => {
    const ret: ChangeLog.ChangeLogChange<{
        scriptCfg: Latest.RuntimeScriptCfg;
    }>[] = [];
    for (const script of newConfig.scripts) {
        const originalScript = originalConfig.scripts.find(s => s.id === script.id);
        if (originalScript && !isEqual(originalScript, script)) {
            ret.push(generateScriptChangeLog({
                action: 'update',
                id: script.id,
                currentScriptValue: script.script,
                originalScriptValue: originalScript,
            }));
        } else if (!originalScript) {
            ret.push(generateScriptChangeLog({
                action: 'add',
                id: script.id,
                currentScriptValue: script.script,
            }));
        }
    }
    for (const oldScript of originalConfig.scripts) {
        if (!newConfig.scripts.find(s => s.id === oldScript.id)) {
            ret.push({
                messages: [
                    {
                        type: 'script',
                        oldScriptValue: oldScript.script,
                        currentScriptValue: undefined,
                    },
                ],
                action: 'delete',
                itemId: oldScript.id,
                previousItem: {
                    scriptCfg: oldScript,
                },
                title: {
                    type: 'interpolate',
                    text: 'A script was removed. It contained the following content:',
                    data: [],
                },
            });
        }
    }
    return ret;
};

export const getAppletDiffereneces = (): ChangeLog.ChangeLogCategory<AppletChangeLogExtraData>[] => {
    const entityData = accessor.entityAsType('appletDesignVersion');
    const originalEntity = accessor.originalEntityAsType('appletDesignVersion');
    if (!originalEntity || !entityData) {
        return [];
    }

    const ret: ChangeLog.ChangeLogCategory<AppletChangeLogExtraData>[] = [];

    const screenChangeLog: ChangeLog.ChangeLogCategory<AppletChangeLogExtraData> = {
        name: 'Screens',
        icon: 'mdi-monitor-dashboard',
        changes: getScreenChanges(originalEntity.config, entityData.config),
        extraData: {
            type: 'screen',
        },
    };
    if (screenChangeLog.changes.length) {
        ret.push(screenChangeLog);
    }

    const macroChangeLog: ChangeLog.ChangeLogCategory<AppletChangeLogExtraData> = {
        name: 'Macros',
        icon: 'mdi-multiplication',
        changes: getMacroChanges(originalEntity.config, entityData.config),
        extraData: {
            type: 'macros',
        },
    };
    if (macroChangeLog.changes.length) {
        ret.push(macroChangeLog);
    }

    const scriptChangeLog: ChangeLog.ChangeLogCategory<AppletChangeLogExtraData> = {
        name: 'Scripts',
        icon: 'mdi-script-text',
        changes: getScriptChanges(originalEntity.config, entityData.config),
        extraData: {
            type: 'scripts',
        },
    };
    if (scriptChangeLog.changes.length) {
        ret.push(scriptChangeLog);
    }

    return ret;
};


export const undoAddition = (changeLogItem: ChangeLog.AddChangeLogData, extraData: AppletChangeLogExtraData) => {
    const newCfg = accessor.entityAsType('appletDesignVersion');
    if (!newCfg) {
        return;
    }
    if (extraData.type === 'screen') {
        const screenToRemove = newCfg.config.screens.find(s => s.id === changeLogItem.itemId);
        if (!screenToRemove) {
            throw new Error('Screen not found when undoing screen addition. Is the screen still in the config?');
        }
        accessor.appletDesign.removeScreen(screenToRemove);
    } else if (extraData.type === 'macros') {
        const macroTargetCell = accessor.appletDesign.findCellInCfg(c => c.type === 'macroTarget' && c.attrs.macroId === changeLogItem.itemId);
        if (!macroTargetCell) {
            throw new Error('Macro cell not found when undoing macro addition. Is the macro cell still in the config?');
        }
        accessor.appletDesign.deleteCell({
            screenId: macroTargetCell.screenId,
            cellCfg: macroTargetCell.cellCfg,
        });
    } else if (extraData.type === 'scripts') {
        // not maintaining this function as the undo action is not currently supported
    } else {
        const _exhaustiveCheck: never = extraData.type;
        throw new Error(`Unhandled extraData type ${JSON.stringify(_exhaustiveCheck)}`);
    }
};

export const undoDeletion = (payload: TypedPreviousChangeLog) => {
    const newCfg = accessor.entityAsType('appletDesignVersion');
    if (!newCfg) {
        return;
    }

    if (payload.type === 'screen') {
        // when a screen is deleted it may also have deleted macros. Those have been stored and need to be restored
        accessor.setPageEntity({
            type: 'appletDesignVersion',
            entity: {
                ...newCfg,
                config: {
                    ...newCfg.config,
                    screens: [
                        ...newCfg.config.screens,
                        payload.changeLogItem.previousItem.screen,
                    ],
                    macros: [
                        ...newCfg.config.macros,
                        ...payload.changeLogItem.previousItem.macros,
                    ],
                },
            },
        });
    } else if (payload.type === 'macros') {
        // when a macro is deleted also deletes the corresponding cell. The cell has been stored and needs to be restored
        const newScreen = newCfg.config.screens.find(s => s.id === payload.changeLogItem.previousItem.cellData.screenId);
        if (!newScreen) {
            return;
        }
        newScreen.cells = [
            ...newScreen.cells,
            ...payload.changeLogItem.previousItem.cellData.cellCfgs.map(c => c.cellCfg),
        ];
        accessor.setPageEntity({
            type: 'appletDesignVersion',
            entity: {
                ...newCfg,
                config: {
                    ...newCfg.config,
                    macros: [
                        ...newCfg.config.macros,
                        payload.changeLogItem.previousItem.macroCfg,
                    ],
                    screens: [
                        ...newCfg.config.screens,
                    ],
                },
            },
        });
    } else if (payload.type === 'scripts') {
        accessor.setPageEntity({
            type: 'appletDesignVersion',
            entity: {
                ...newCfg,
                config: {
                    ...newCfg.config,
                    scripts: [
                        ...newCfg.config.scripts,
                        payload.changeLogItem.previousItem.scriptCfg,
                    ],
                },
            },
        });
    } else {
        const _exhaustiveCheck: never = payload;
        throw new Error(`Unhandled payload type ${JSON.stringify(_exhaustiveCheck)}`);
    }
};

export const undoUpdate = (payload: TypedPreviousChangeLog) => {
    const newCfg = accessor.entityAsType('appletDesignVersion');
    if (!newCfg) {
        return;
    }
    if (payload.type === 'screen') {
        const screenIdx = newCfg.config.screens.findIndex(s => s.id === payload.changeLogItem.itemId);
        if (screenIdx === -1) {
            throw new Error('Screen not found when undoing screen update. Is the screen still in the config?');
        }

        const screenThatIsBeingRemoved = newCfg.config.screens[screenIdx];
        screenThatIsBeingRemoved.cells.filter((c): c is Latest.Screen.Cells.Btn.Cell => c.type === 'btn').forEach(cell => {
            if (cell.attrs.linkedScriptId) {
                const scriptIdxInTheCfg = newCfg.config.scripts.findIndex(s => s.id === cell.attrs.linkedScriptId);
                if (scriptIdxInTheCfg === -1) {
                    throw new Error('Script not found when undoing screen update. Is the script still in the config?');
                }
                const originalScript = payload.changeLogItem.previousItem.scripts.find(s => s.id === cell.attrs.linkedScriptId);
                if (originalScript) {
                    newCfg.config.scripts.splice(scriptIdxInTheCfg, 1, originalScript);
                } else {
                    newCfg.config.scripts.splice(scriptIdxInTheCfg, 1);
                }
            }
        });
        screenThatIsBeingRemoved.cells.filter((c): c is Latest.Screen.Cells.MacroTargetCell => c.type === 'macroTarget').forEach(cell => {
            const macroIdxInTheCfg = newCfg.config.macros.findIndex(m => m.id === cell.attrs.macroId);
            if (macroIdxInTheCfg === -1) {
                throw new Error('Macro not found when undoing screen update. Is the macro still in the config?');
            }

            const originalMacro = payload.changeLogItem.previousItem.macros.find(m => m.id === cell.attrs.macroId);
            if (originalMacro) {
                newCfg.config.macros.splice(macroIdxInTheCfg, 1, originalMacro);
            } else {
                newCfg.config.macros.splice(macroIdxInTheCfg, 1);
            }
        });
        newCfg.config.screens.splice(screenIdx, 1, payload.changeLogItem.previousItem.screen);
        accessor.setPageEntity({
            type: 'appletDesignVersion',
            entity: {
                ...newCfg,
                config: {
                    ...newCfg.config,
                },
            },
        });
    } else if (payload.type === 'macros') {
        const newScreen = newCfg.config.screens[payload.changeLogItem.previousItem.cellData.screenIdx];
        for (const cellData of payload.changeLogItem.previousItem.cellData.cellCfgs) {
            newScreen.cells.splice(cellData.cellIdx, 1, cellData.cellCfg);
        }
        newCfg.config.screens.splice(payload.changeLogItem.previousItem.cellData.screenIdx, 1, newScreen);
        const macroIdx = newCfg.config.macros.findIndex(m => m.id === payload.changeLogItem.itemId);
        newCfg.config.macros.splice(macroIdx, 1, payload.changeLogItem.previousItem.macroCfg);
        accessor.setPageEntity({
            type: 'appletDesignVersion',
            entity: {
                ...newCfg,
                config: {
                    ...newCfg.config,
                },
            },
        });
    } else if (payload.type === 'scripts') {
        newCfg.config.scripts = newCfg.config.scripts.map(s => {
            if (s.id === payload.changeLogItem.itemId) {
                return payload.changeLogItem.previousItem.scriptCfg;
            }
            return s;
        });
        accessor.setPageEntity({
            type: 'appletDesignVersion',
            entity: {
                ...newCfg,
                config: {
                    ...newCfg.config,
                },
            },
        });
    } else {
        const _exhaustiveCheck: never = payload;
        throw new Error(`Unhandled payload type ${JSON.stringify(_exhaustiveCheck)}`);
    }
};
